import { useMemo, useState } from "react";
import { supabase } from "@/lib/supabase";
import { useToast } from "@/components/feature/Toast";
import EmptyState from "@/pages/admin/dashboard/components/EmptyState";
import { timeAgo } from "@/pages/admin/dashboard/types";
import type {
  AdminProduct,
  AdminProfile,
  AdminReport,
  ReportStatus,
} from "@/pages/admin/dashboard/types";

interface ReportsTabProps {
  reports: AdminReport[];
  products: AdminProduct[];
  profiles: AdminProfile[];
  onRefresh: () => void;
}

const STATUS_META: Record<ReportStatus, { label: string; badge: string }> = {
  pending: { label: "Pendiente", badge: "bg-accent-100 text-accent-800" },
  reviewed: { label: "Revisado", badge: "bg-secondary-100 text-secondary-700" },
  dismissed: {
    label: "Descartado",
    badge: "bg-background-100 text-foreground-600",
  },
};

export default function ReportsTab({
  reports,
  products,
  profiles,
  onRefresh,
}: ReportsTabProps) {
  const { showToast } = useToast();
  const [busyId, setBusyId] = useState<number | null>(null);

  const productTitleById = useMemo(() => {
    const map = new Map<number, string>();
    products.forEach((p) => map.set(p.id, p.title));
    return map;
  }, [products]);

  const reporterNameById = useMemo(() => {
    const map = new Map<string, string>();
    profiles.forEach((p) => map.set(p.id, p.full_name ?? p.email ?? "Usuario"));
    return map;
  }, [profiles]);

  const updateStatus = async (report: AdminReport, status: ReportStatus) => {
    setBusyId(report.id);
    const { error } = await supabase
      .from("reports")
      .update({ status })
      .eq("id", report.id);
    setBusyId(null);
    if (error) {
      showToast("No se pudo actualizar el reporte.", "error");
      return;
    }
    showToast(
      status === "reviewed"
        ? "Reporte marcado como revisado."
        : "Reporte descartado.",
      "success"
    );
    onRefresh();
  };

  if (reports.length === 0) {
    return (
      <EmptyState
        icon="ri-alert-line"
        title="Sin reportes"
        description="No hay reportes por revisar en este momento."
      />
    );
  }

  return (
    <ul className="divide-y divide-background-200/60">
      {reports.map((report) => {
        const meta = STATUS_META[report.status];
        const isPending = report.status === "pending";
        return (
          <li
            key={report.id}
            className="flex flex-col gap-3 p-5 sm:flex-row sm:items-start"
          >
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-accent-100">
              <i className="ri-flag-2-line text-lg text-accent-700"></i>
            </span>

            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <p className="text-sm font-semibold text-foreground-950">
                  {report.reason}
                </p>
                <span
                  className={`inline-flex items-center gap-1 whitespace-nowrap rounded-full px-2.5 py-0.5 text-xs font-medium ${meta.badge}`}
                >
                  {meta.label}
                </span>
              </div>
              {report.details && (
                <p className="mt-1 text-sm leading-relaxed text-foreground-600">
                  {report.details}
                </p>
              )}
              <p className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-foreground-500">
                <span className="flex items-center gap-1">
                  <i className="ri-store-2-line"></i>
                  {productTitleById.get(report.product_id) ?? "Publicación eliminada"}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-user-line"></i>
                  {reporterNameById.get(report.reporter_id) ?? "Usuario"}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-time-line"></i>
                  {timeAgo(report.created_at)}
                </span>
              </p>
            </div>

            {isPending && (
              <div className="flex shrink-0 gap-2 sm:flex-col lg:flex-row">
                <button
                  type="button"
                  disabled={busyId === report.id}
                  onClick={() => updateStatus(report, "reviewed")}
                  className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md bg-secondary-500 px-3 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-secondary-600 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  <i className="ri-check-line"></i>
                  Revisar
                </button>
                <button
                  type="button"
                  disabled={busyId === report.id}
                  onClick={() => updateStatus(report, "dismissed")}
                  className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md border border-background-200 px-3 py-1.5 text-xs font-semibold text-foreground-600 transition-colors hover:bg-background-100 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  <i className="ri-close-line"></i>
                  Descartar
                </button>
              </div>
            )}
          </li>
        );
      })}
    </ul>
  );
}